import { useEffect, useMemo, useState } from 'react';

import { faVolume } from '@fortawesome/pro-duotone-svg-icons';
import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { useBooleanState } from 'webrix/hooks';

import { RINGTONE_OPTIONS } from '@cloud-wave/neon-common-lib/app';

import { useAgentContext } from 'lib/common/contexts/AgentContext';
import { useContactContext } from 'lib/common/contexts/ContactContext';
import { usePreferencesContext } from 'lib/common/contexts/PreferencesContext';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import { useLocalStorage } from 'lib/common/hooks/useLocalStorage';
import useTrigger from 'lib/common/hooks/useTrigger';

import Accordion from 'lib/common/components/Accordion';
import Button from 'lib/common/components/Button/Button';
import Label from 'lib/common/components/Label';
import { LogEvents, logger } from 'lib/common/components/LoggerController';
import PhoneInput from 'lib/common/components/PhoneInput/containers/PhoneInput';
import ReactSelect from 'lib/common/components/ReactSelect';
import PreferenceSectionContainer from 'lib/common/components/molecules/Preferences/components/PreferenceSectionContainer';
import {
  AGENT_PREFERENCES,
  DEFAULT_DEVICE,
  PHONE_OPTIONS,
} from 'lib/common/components/molecules/Preferences/constants';
import { getDeviceOptionsByType } from 'lib/common/components/molecules/Preferences/helpers';

import AUSTRALIA_PHONE_EXTENSION from 'lib/common/constants/defaultPhoneNumberExtension';

import connectGetter from 'lib/common/utils/connectGetter';

import RingtonePreference from './components/RingtonePreference';

interface IPreferencesAudioDevices {}

function getDefaultDevice(agentConfig, key, getStorageItem) {
  return agentConfig ? getStorageItem(`${agentConfig?.username}:${key}`) || DEFAULT_DEVICE : DEFAULT_DEVICE;
}

const PreferencesAudioDevices = ({}: IPreferencesAudioDevices) => {
  const { isSoftphone } = useLayout();
  const { agent } = useAgentContext();
  const {
    state: { onActiveCall, hasConnectingTask },
  } = useContactContext();

  const {
    state: { ringtone },
    actions: { setRingtone },
  } = usePreferencesContext();

  const ringtoneAudio = useMemo(() => new Audio(RINGTONE_OPTIONS[ringtone].value), [ringtone]);

  const [isSoftphoneEnabled, setIsSoftphoneEnabled] = useState(connectGetter(agent, 'isSoftphoneEnabled') || false);
  const [phoneInputTrigger, updatePhoneInputTrigger] = useTrigger();
  const { getStorageItem, setStorageItem } = useLocalStorage();

  const agentConfig = connectGetter(agent, 'getConfiguration');

  const connect = (window as any).getConnect();

  const getDefaultPhoneNumber = () => connectGetter(agent, 'getExtension') || AUSTRALIA_PHONE_EXTENSION;

  const [phoneNumber, setPhoneNumber] = useState(getDefaultPhoneNumber());
  const [deskPhoneButtonsVisible, setDeskPhoneButtonsVisible] = useState(false);

  const {
    value: ringtonePlaying,
    toggle: toggleRingtonePlaying,
    setFalse: stopRingtonePlaying,
  } = useBooleanState(false);

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  const [speakerDevice, setSpeakerDevice] = useState(getDefaultDevice(agentConfig, 'speakerDevice', getStorageItem));
  const [microphoneDevice, setMicrophoneDevice] = useState(
    getDefaultDevice(agentConfig, 'microphoneDevice', getStorageItem)
  );
  const [ringerDevice, setRingerDevice] = useState(getDefaultDevice(agentConfig, 'ringerDevice', getStorageItem));

  const handleSpeakerChange = (option) => {
    setSpeakerDevice(option.value);
    agent?.setSpeakerDevice(option.value);
    setStorageItem(`${agentConfig?.username}:speakerDevice`, option.value);
  };

  const handleMicrophoneChange = (option) => {
    setMicrophoneDevice(option.value);
    agent?.setMicrophoneDevice(option.value);
    setStorageItem(`${agentConfig?.username}:microphoneDevice`, option.value);
  };

  const handleRingerChange = (option) => {
    setRingerDevice(option.value);
    agent?.setRingerDevice(option.value);
    setStorageItem(`${agentConfig?.username}:ringerDevice`, option.value);
  };

  const outputDevicesOptions = getDeviceOptionsByType(devices, 'audiooutput');
  const inputDevicesOptions = getDeviceOptionsByType(devices, 'audioinput');

  const handleSoftPhoneChange = (newValue) => {
    const isSoftPhoneEnabled = newValue?.value === PHONE_OPTIONS[0].value;

    if (!agentConfig) {
      return;
    }

    if (isSoftPhoneEnabled) {
      agent?.setConfiguration({
        ...agentConfig,
        softphoneEnabled: true,
        agentPreferences: AGENT_PREFERENCES,
      });
    }

    const savedPhoneNumber = connectGetter(agent, 'getExtension') || AUSTRALIA_PHONE_EXTENSION;
    setPhoneNumber(savedPhoneNumber);

    if (!isSoftPhoneEnabled && parsePhoneNumber(savedPhoneNumber).valid) {
      agent?.setConfiguration({
        ...agentConfig,
        softphoneEnabled: false,
        extension: savedPhoneNumber,
        agentPreferences: AGENT_PREFERENCES,
      });
      setDeskPhoneButtonsVisible(false);
    }

    setIsSoftphoneEnabled(isSoftPhoneEnabled);
  };

  const handleDeskPhoneSet = () => {
    if (!agentConfig) {
      return;
    }

    agent?.setConfiguration({
      ...agentConfig,
      softphoneEnabled: false,
      extension: phoneNumber,
      agentPreferences: AGENT_PREFERENCES,
    });

    setIsSoftphoneEnabled(false);
    setDeskPhoneButtonsVisible(false);
  };

  useEffect(() => {
    if (!hasConnectingTask) {
      return;
    }

    agent?.setRingerDevice(ringerDevice);
  }, [hasConnectingTask]);

  useEffect(() => {
    if (!onActiveCall) {
      return;
    }

    agent?.setSpeakerDevice(speakerDevice);
    agent?.setMicrophoneDevice(microphoneDevice);
  }, [onActiveCall]);

  useEffect(() => {
    (async () => {
      try {
        const framedMediaDevices: MediaDeviceInfo[] = await connect.core.getFrameMediaDevices(30000);
        setDevices(framedMediaDevices);
      } catch (error) {
        logger.error(LogEvents.MEDIA_DEVICES_LOAD_FAILED, { error });
      }
    })();

    ringtoneAudio?.addEventListener('ended', stopRingtonePlaying);

    return () => {
      ringtoneAudio?.removeEventListener('ended', stopRingtonePlaying);
    };
  }, []);

  const phoneValue = isSoftphoneEnabled ? PHONE_OPTIONS[0] : PHONE_OPTIONS[1];

  return (
    <PreferenceSectionContainer icon={faVolume} title={'Audio & Devices'}>
      <Accordion title="Phone" defaultOpen={isSoftphone}>
        <ReactSelect
          aria-label="Phone"
          classNamePrefix="react-select"
          isSearchable={false}
          id="Phone"
          options={PHONE_OPTIONS}
          value={phoneValue}
          onChange={handleSoftPhoneChange}
        />
        {!isSoftphoneEnabled && (
          <div className="preferences__phone-number__container">
            <div className="preferences__phone-number__container__input">
              <PhoneInput
                aria-label="Phone number"
                key={phoneInputTrigger}
                initialValue={phoneNumber}
                onChange={(number) => {
                  setDeskPhoneButtonsVisible(true);
                  setPhoneNumber(number);
                }}
                endInputAdornment={
                  deskPhoneButtonsVisible && (
                    <>
                      <Button
                        ariaLabel="Set default phone number"
                        styleType="SECONDARY"
                        onClick={() => {
                          setPhoneNumber(getDefaultPhoneNumber());
                          updatePhoneInputTrigger();
                          setDeskPhoneButtonsVisible(false);
                        }}
                        icon={faXmark}
                        size="medium"
                      />
                      <Button
                        ariaLabel="Set desk phone"
                        className="ml-5 mr-5"
                        onClick={handleDeskPhoneSet}
                        disabled={!parsePhoneNumber(phoneNumber).valid}
                        icon={faCheck}
                        size="medium"
                      />
                    </>
                  )
                }
              />
            </div>
          </div>
        )}
        {isSoftphoneEnabled && (
          <RingtonePreference
            setRingtone={setRingtone}
            stopRingtonePlaying={stopRingtonePlaying}
            toggleRingtonePlaying={toggleRingtonePlaying}
            ringtoneAudio={ringtoneAudio}
            ringtonePlaying={ringtonePlaying}
            ringtone={ringtone}
          />
        )}
      </Accordion>
      <Accordion title="Audio Devices">
        {inputDevicesOptions.some((deviceOption) => deviceOption.label) && (
          <>
            <Label text="Speaker" id="Speaker" className="mb-10">
              <ReactSelect
                aria-label="Speaker"
                classNamePrefix="react-select"
                isSearchable={false}
                id="Speaker"
                options={outputDevicesOptions}
                value={outputDevicesOptions.find((device) => device.value === speakerDevice)}
                onChange={handleSpeakerChange}
              />
            </Label>
            <Label text="Microphone" id="Microphone" className="mb-10">
              <ReactSelect
                aria-label="Microphone"
                classNamePrefix="react-select"
                isSearchable={false}
                id="Microphone"
                options={inputDevicesOptions}
                value={inputDevicesOptions.find((device) => device.value === microphoneDevice)}
                onChange={handleMicrophoneChange}
              />
            </Label>
            <Label text="Ringer" id="Ringer">
              <ReactSelect
                aria-label="Ringer"
                classNamePrefix="react-select"
                isSearchable={false}
                id="Ringer"
                options={outputDevicesOptions}
                value={outputDevicesOptions.find((device) => device.value === ringerDevice)}
                onChange={handleRingerChange}
              />
            </Label>
          </>
        )}
      </Accordion>
    </PreferenceSectionContainer>
  );
};

export default PreferencesAudioDevices;
